import React, { useEffect, useState } from 'react';
import { NavLink, Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Button from '../components/Button';
import KeyboardShortcutsHelp from '../components/KeyboardShortcutsHelp';
import ScreenGuard from '../components/ScreenGuard';
import '../utils/themeInit';
import { getUnreadCount } from '../services/notificationsApi';

const navGroups = [
  {
    label: 'Overview',
    items: [
      { to: '/', label: 'Dashboard', end: true },
    ],
  },
  {
    label: 'Analysis',
    items: [
      { to: '/stock-analysis', label: 'Stock Analysis' },
      { to: '/order-analysis', label: 'Order Analysis' },
      { to: '/dispatch-analysis', label: 'Dispatch Analysis' },
      { to: '/production-planning', label: 'Production Planning' },
        { to: '/price-list', label: 'Price List' },
        { to: '/complaints', label: 'Complaints' },
    ],
  },
  {
    label: 'AI Engines',
    items: [
      { to: '/demand-sensing', label: 'Demand Sensing' },
      { to: '/forecasting', label: 'Forecasting' },
      { to: '/recommendations', label: 'Recommendations' },
      { to: '/optimization', label: 'Optimization' },
      { to: '/classification', label: 'Classification' },
      { to: '/analytics', label: 'Analytics & MIS' },
      { to: '/executive-summary', label: 'Executive Summary' },
    ],
  },
  {
    label: 'Data',
    items: [
      { to: '/reports', label: 'Reports Center' },
      { to: '/imports/order', label: 'Order Import' },
      { to: '/imports/stock', label: 'Stock Import' },
      { to: '/imports/dispatch', label: 'Dispatch Import' },
      { to: '/imports/production', label: 'Production Import' },
    ],
  },
  {
    label: 'Administration',
    items: [
      { to: '/users', label: 'User Management' },
      { to: '/login-activity', label: 'Login Activity' },
      { to: '/audit-log', label: 'Audit Log' }, // AUDIT-LOG v1
      { to: '/data-management', label: 'Data Management' },
      { to: '/settings', label: 'Settings' },
    ],
  },
];

const POLL_INTERVAL_MS = 30000;

// A user limited to one Ref. Party only gets these two screens (the server enforces the same).
const RESTRICTED_NAV = new Set(['/growth', '/order-analysis/design-need', '/', '/stock-analysis', '/order-analysis', '/dispatch-analysis', '/complaints', '/price-list', '/inactive-customers']);

// Which module's ".view" permission opens each menu entry.
const NAV_MODULES = {
  '/': ['dashboard'],
  '/notifications': ['notifications'],
  '/stock-analysis': ['stock_analysis'],
  '/order-analysis': ['order_analysis'],
  '/dispatch-analysis': ['dispatch_analysis'],
  '/production-planning': ['production_planning'],
    '/price-list': ['price_list'], '/complaints': ['complaints'],
  '/demand-sensing': ['demand_sensing'],
  '/forecasting': ['forecasting'],
  '/recommendations': ['ai_recommendations'],
  '/optimization': ['ai_optimization'],
  '/classification': ['classification_center'],
  '/analytics': ['analytics_mis'],
  '/executive-summary': ['executive_summary'],
  '/reports': ['reports_center'],
  '/data-management': ['system_config', 'excel_import'],
  '/users': ['user_management'],
  '/login-activity': ['login_activity'],
  '/settings': ['system_config'],
};

function navModules(to) {
  if (to.startsWith('/imports/')) return ['excel_import'];
  return NAV_MODULES[to] || [];
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const location = useLocation();
  const restricted = Boolean(user && user.refPartyId);
  // Menu: only what this user may open (older sessions without a permission list see everything).
  const permissions = user && Array.isArray(user.permissions) ? new Set(user.permissions) : null;
  const canSee = (item) => {
    if (restricted && !RESTRICTED_NAV.has(item.to)) return false;
    if (item.to === '/audit-log') return Boolean(user && user.role && ['admin', 'super_admin'].includes(user.role.code)); // AUDIT-LOG v1: admin only
    if (!permissions) return true;
    const modules = navModules(item.to);
    return modules.length === 0 || modules.some((m) => permissions.has(`${m}.view`));
  };
  const visibleGroups = navGroups
    .map((g) => ({ ...g, items: g.items.filter(canSee) }))
    .filter((g) => g.items.length > 0);

  useEffect(() => {
    if (restricted) return undefined;
    let cancelled = false;
    const poll = () => {
      getUnreadCount()
        .then((count) => {
          if (!cancelled) setUnreadCount(count);
        })
        .catch(() => {});
    };
    poll();
    const interval = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [restricted]);

  // Navigating to a new page should close the mobile drawer — staying open
  // over the new page's content would just be in the way.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  if (restricted && !RESTRICTED_NAV.has(location.pathname)) {
    return <Navigate to="/" replace />;
  }

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      {user && user.watermarkEnabled && <ScreenGuard user={user} />}
      <a href="#main" className="skip-link">
        Skip to content
      </a>

      <button
        type="button"
        className="app-menu-toggle"
        aria-label={mobileNavOpen ? 'Close menu' : 'Open menu'}
        aria-expanded={mobileNavOpen}
        onClick={() => setMobileNavOpen((v) => !v)}
        style={{
          position: 'fixed',
          top: 'var(--space-3)',
          left: 'var(--space-3)',
          zIndex: 60,
          width: 40,
          height: 40,
          alignItems: 'center',
          justifyContent: 'center',
          border: 'none',
          borderRadius: 'var(--radius-sm)',
          background: 'var(--color-structural)',
          color: 'var(--color-structural-text)',
          boxShadow: 'var(--shadow-card)',
          cursor: 'pointer',
          fontSize: 'var(--font-size-lg)',
        }}
      >
        {mobileNavOpen ? '\u2715' : '\u2630'}
      </button>

      <div
        className={`app-sidebar-backdrop${mobileNavOpen ? ' app-sidebar-backdrop-open' : ''}`}
        onClick={() => setMobileNavOpen(false)}
        aria-hidden="true"
      />

      <nav
        aria-label="Primary"
        className={`app-sidebar${mobileNavOpen ? ' app-sidebar-open' : ''}`}
        style={{
          width: 232,
          flexShrink: 0,
          background: 'var(--color-structural)',
          boxShadow: 'var(--shadow-structural)',
          padding: 'var(--space-4) var(--space-3)',
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 12px 20px' }}>
          <svg width="22" height="22" viewBox="0 0 22 22" aria-hidden="true">
            <rect x="1" y="1" width="9" height="9" rx="1.5" fill="var(--color-structural-text-muted)" opacity="0.35" />
            <rect x="12" y="1" width="9" height="9" rx="1.5" fill="var(--color-structural-text-muted)" opacity="0.35" />
            <rect x="1" y="12" width="9" height="9" rx="1.5" fill="var(--color-structural-text-muted)" opacity="0.35" />
            <rect x="12" y="12" width="9" height="9" rx="1.5" fill="var(--color-accent)" />
          </svg>
          <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 15, color: 'var(--color-structural-text)', letterSpacing: '-0.01em' }}>
            Dispatch Analysis
          </span>
        </div>

        {visibleGroups.map((group) => (
          <div key={group.label}>
            <div className="nav-group-label">{group.label}</div>
            {group.items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) => `nav-link${isActive ? ' nav-link-active' : ''}`}
              >
                {item.label}
                {item.showBadge && unreadCount > 0 && (
                  <span
                    style={{
                      background: 'var(--color-danger)',
                      color: '#fff',
                      borderRadius: 10,
                      fontSize: 10,
                      fontWeight: 700,
                      padding: '2px 6px',
                      minWidth: 16,
                      textAlign: 'center',
                    }}
                  >
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </NavLink>
            ))}
          </div>
        ))}

        <div style={{ marginTop: 'auto', paddingTop: 'var(--space-5)', borderTop: '1px solid var(--color-structural-border)', marginLeft: -12, marginRight: -12, paddingLeft: 12, paddingRight: 12 }}>
          {user && (
            <div style={{ padding: '12px 12px 12px', marginBottom: 'var(--space-2)' }}>
              <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-structural-text)' }}>
                {user.fullName}
              </div>
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-structural-text-muted)' }}>
                {user.role.name}
              </div>
            </div>
          )}
          <Button variant="secondary" style={{ width: '100%' }} onClick={logout}>
            Log out
          </Button>
        </div>
      </nav>
      <main id="main" className="app-main" style={{ flex: 1, padding: 'var(--space-6)', overflowY: 'auto' }}>
        <Outlet />
      </main>
      <KeyboardShortcutsHelp />
    </div>
  );
}
